import { t } from '../i18n';
export const INTEGRATION_UPGRADE_START_BLOCK = 323;
export const INTEGRATION_UPGRADE_COMMIT_BLOCK = 324;
export const INTEGRATION_FROM_BLOCK = INTEGRATION_UPGRADE_START_BLOCK;

export interface IntegrationLedgerTransaction {
  transaction_id: string;
  block_number: string;
  transaction_index: number;
  sender: string | null;
  receiver: string | null;
  item: string | null;
  timestamp: string | null;
  status: string | null;
  creator_msp_id: string | null;
  chaincode_name: string | null;
  chaincode_version: string | null;
  record_type: string | null;
  business_id_type: string | null;
  business_id: string | null;
  operation_type: string | null;
  payload_hash: string | null;
  payload_size_bytes: number | null;
  payload_storage_type: string | null;
  payload_ref: string | null;
  block_hash: string | null;
  previous_hash: string | null;
  data_hash: string | null;
  transaction_count: number | string | null;
  block_created_at: string;
  function_name: string | null;
  arg1: string | null;
  arg2: string | null;
  arg3: string | null;
  arg4: string | null;
  arg5: string | null;
  arg6: string | null;
  arg7: string | null;
  arg8: string | null;
  arg9: string | null;
  arg10: string | null;
}

export interface PayloadProof {
  hashMatches?: boolean;
  evidenceMatches?: boolean;
  verificationStatus?: 'VERIFIED' | 'MISMATCH' | 'PAYLOAD_UNAVAILABLE' | 'UNAVAILABLE';
  verificationSource?: string;
  topLevelKeys?: string[];
  rawPayload?: Record<string, any>;
  status?: string;
  blockNumber?: string;
}

export function payloadProofFromEvidenceError(code: unknown, message: unknown): PayloadProof | null {
  const status = typeof message === 'string' ? message : undefined;

  if (code === 'LEDGER_OFFCHAIN_EVIDENCE_MISMATCH') {
    return {
      verificationStatus: 'MISMATCH',
      evidenceMatches: false,
      hashMatches: false,
      status
    };
  }
  if (code === 'OFFCHAIN_PAYLOAD_NOT_FOUND') {
    return {
      verificationStatus: 'PAYLOAD_UNAVAILABLE',
      evidenceMatches: false,
      status
    };
  }
  if (code === 'LEDGER_EVIDENCE_UNAVAILABLE') {
    return {
      verificationStatus: 'UNAVAILABLE',
      evidenceMatches: false,
      status
    };
  }

  return null;
}

export interface IntegrationLedgerSnapshot {
  fromBlock: number;
  limit: number;
  count: number;
  earliestBlock: number | null;
  latestBlock: number | null;
  signature: string;
  emittedAt: string;
  source: string;
  message?: string;
  transactions?: IntegrationLedgerTransaction[];
}

export interface IntegrationBlockGroup {
  blockNumber: string;
  rows: IntegrationLedgerTransaction[];
  representative: IntegrationLedgerTransaction;
}

export interface NetworkStats {
  latestBlockNumber: number;
  totalTransactions: number;
}

export type IntegrationTxCategory = 'lifecycle' | 'recordIntegrationResult' | 'legacyRecordEvent' | 'other';

export function formatBytes(bytes?: number | null) {
  if (typeof bytes !== 'number') return t('Not measured');
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function formatTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

export function formatRelativeTimeKo(value?: string | null) {
  if (!value) return t("No record yet");
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return t("No record yet");

  const diffMinutes = Math.max(0, Math.floor((Date.now() - timestamp) / (1000 * 60)));
  if (diffMinutes < 1) return t("Just now");
  if (diffMinutes < 60) return t("{0} min ago", [diffMinutes]);

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t("{0} hr ago", [diffHours]);

  const diffDays = Math.floor(diffHours / 24);
  return t("{0} days ago", [diffDays]);
}

export function koreaDateKey(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

export function blockNumberValue(value?: string | null) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function shortHash(value?: string | null, head = 10, tail = 8) {
  if (!value) return '-';
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}...${value.slice(-tail)}`;
}

export function shortBusinessId(value?: string | null) {
  if (!value) return '-';
  if (value.length <= 34) return value;
  return `${value.slice(0, 24)}...${value.slice(-8)}`;
}

export function normalizeOrg(value?: string | null) {
  if (!value) return '';
  const upper = value.toUpperCase();
  if (upper === 'EINS' || upper === 'EINSMSP' || upper === 'CFS2MSP') return 'EINS';
  if (upper === 'KAIST' || upper === 'KAISTMSP' || upper === 'CY1MSP') return 'KAIST';
  if (upper === 'KULS' || upper === 'KULSMSP' || upper === 'CFS1MSP') return 'KULS';
  return value;
}

export function sourceOrgOf(tx: IntegrationLedgerTransaction) {
  return normalizeOrg(tx.sender || tx.creator_msp_id);
}

export function targetOrgOf(tx: IntegrationLedgerTransaction) {
  return normalizeOrg(tx.receiver);
}

export function isIntegrationBusinessTx(tx: IntegrationLedgerTransaction) {
  return classifyIntegrationTx(tx) !== 'lifecycle';
}

export function sortIntegrationTransactions(rows: IntegrationLedgerTransaction[]) {
  return rows.slice().sort((a, b) => {
    const blockDiff = blockNumberValue(b.block_number) - blockNumberValue(a.block_number);
    if (blockDiff !== 0) return blockDiff;
    return (b.transaction_index || 0) - (a.transaction_index || 0);
  });
}

export function classifyIntegrationTx(tx: IntegrationLedgerTransaction): IntegrationTxCategory {
  if (tx.chaincode_name === '_lifecycle') return 'lifecycle';
  if (tx.function_name === 'RecordIntegrationResult') return 'recordIntegrationResult';
  if (tx.function_name === 'RecordEvent') return 'legacyRecordEvent';
  return 'other';
}

export function categoryLabel(tx: IntegrationLedgerTransaction) {
  const category = classifyIntegrationTx(tx);
  if (category === 'lifecycle') return t("Chaincode upgrade");
  if (category === 'recordIntegrationResult') return t("New v1 record");
  if (category === 'legacyRecordEvent') return t("Legacy compatibility");
  return t("Other");
}

export function categoryColor(tx: IntegrationLedgerTransaction): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
  const category = classifyIntegrationTx(tx);
  if (category === 'lifecycle') return 'secondary';
  if (category === 'recordIntegrationResult') return 'success';
  if (category === 'legacyRecordEvent') return 'info';
  return 'default';
}

export function safeParsePayloadJSON(value: string | null) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function legacyPayload(tx: IntegrationLedgerTransaction) {
  return tx.function_name === 'RecordEvent' ? safeParsePayloadJSON(tx.arg10) : null;
}

export function liveOperation(tx: IntegrationLedgerTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') return tx.operation_type || '-';
  if (tx.function_name === 'RecordEvent') return tx.arg9 || tx.operation_type || tx.item || '-';
  if (tx.function_name === 'ApproveChaincodeDefinitionForMyOrg') return 'approve';
  if (tx.function_name === 'CommitChaincodeDefinition') return 'commit';
  return tx.operation_type || tx.item || '-';
}

export function isValidationOperation(value?: string | null) {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized === 'validation_opt' || normalized === 'test' || normalized.includes('validation');
}

export function operationDisplayLabel(value?: string | null) {
  const operation = value || '-';
  return isValidationOperation(operation) ? 'Validation/Test' : operation;
}

export function liveBusinessId(tx: IntegrationLedgerTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') {
    return tx.business_id_type && tx.business_id ? `${tx.business_id_type}: ${tx.business_id}` : '-';
  }
  if (tx.function_name === 'RecordEvent') {
    const payload = legacyPayload(tx);
    if (payload?.request_id) return `request_id: ${payload.request_id}`;
    return tx.arg1 ? `recordId: ${tx.arg1}` : '-';
  }
  return '-';
}

export function livePayloadSizeBytes(tx: IntegrationLedgerTransaction) {
  if (typeof tx.payload_size_bytes === 'number') return tx.payload_size_bytes;
  if (tx.function_name === 'RecordEvent' && tx.arg10) {
    return new TextEncoder().encode(tx.arg10).length;
  }
  return undefined;
}

export function liveRecordType(tx: IntegrationLedgerTransaction) {
  if (tx.function_name === 'RecordIntegrationResult') return tx.record_type || 'New v1 record';
  if (tx.function_name === 'RecordEvent') return 'Legacy RecordEvent';
  return tx.function_name || '-';
}

export function ledgerStatusLabel(tx: IntegrationLedgerTransaction) {
  if (tx.chaincode_name === '_lifecycle') return t("Lifecycle committed");
  if (tx.block_number && tx.transaction_id) return t("Committed to ledger");
  return t("Pending ledger confirmation");
}

export function indexStatusLabel(tx: IntegrationLedgerTransaction) {
  if (tx.block_number && tx.transaction_id) return t("Indexed in WebApp");
  return t("Indexing pending");
}

export function integrityStatusLabel(tx: IntegrationLedgerTransaction, proof?: PayloadProof) {
  const category = classifyIntegrationTx(tx);
  if (category === 'recordIntegrationResult') {
    if (proof?.verificationStatus === 'VERIFIED') return t("Verified against peer ledger");
    if (proof?.verificationStatus === 'MISMATCH') return t("Evidence mismatch");
    if (proof?.verificationStatus === 'PAYLOAD_UNAVAILABLE' || proof?.verificationStatus === 'UNAVAILABLE') {
      return t("Verification unavailable");
    }
    if (proof?.hashMatches === true) return t("Verified");
    if (proof?.hashMatches === false) return t("Hash mismatch");
    return t("Check on open");
  }
  if (category === 'legacyRecordEvent') return t("No hash-ref evidence");
  if (category === 'lifecycle') return t("Not a business payload");
  return t("Not applicable");
}

export function recordStatusLabel(tx: IntegrationLedgerTransaction) {
  const status = tx.status?.trim();
  if (!status) return t("No business status");
  if (status.toUpperCase() === 'SUBMITTED') return t("Business record submitted");
  return t("Business status: {0}", [status]);
}

export function buildIntegrationBlockGroups(rows: IntegrationLedgerTransaction[]) {
  const grouped = new Map<string, IntegrationLedgerTransaction[]>();
  rows.forEach((tx) => {
    const key = tx.block_number || '-';
    grouped.set(key, [...(grouped.get(key) || []), tx]);
  });

  return Array.from(grouped.entries())
    .map(([blockNumber, blockRows]) => {
      const sortedRows = blockRows.slice().sort((a, b) => (b.transaction_index || 0) - (a.transaction_index || 0));
      return {
        blockNumber,
        rows: sortedRows,
        representative: sortedRows[0]
      };
    })
    .sort((a, b) => blockNumberValue(b.blockNumber) - blockNumberValue(a.blockNumber));
}

export function operationFilterOptions(rows: IntegrationLedgerTransaction[]) {
  const canonical = ['Daily_Opt', 'Emergency_Opt', 'assignment', 'retrieval', 'relocation'];
  const values = new Set(rows.map((tx) => liveOperation(tx)).filter((value) => value && value !== '-'));
  return canonical.filter((value) => values.has(value));
}

export function todayKstRows(rows: IntegrationLedgerTransaction[]) {
  const today = koreaDateKey(new Date().toISOString());
  return rows.filter((tx) => koreaDateKey(tx.block_created_at) === today);
}
